import React, { useEffect, useMemo, useState } from "react";
import { DollarSign, TrendingUp, TrendingDown, Activity, Coins, Gem, CalendarDays, Bitcoin, LineChart, Building2, Landmark } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const EN_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const TR_MONTHS = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

function formatPhoneDate(d, lang) {
  const months = lang === "tr" ? TR_MONTHS : EN_MONTHS;
  const day = d.getDate();
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} · ${hh}:${mm}`;
}

// Base reference rates (Feb 2026 realistic values)
const BASE = {
  usd_try: 32.50,
  eur_try: 35.20,
  eur_usd: 1.083,
  gbp_try: 41.10,
  // Extended forex — Feb 2026 realistic vs TRY
  cny_try: 4.52,               // Chinese Yuan
  jpy_try: 0.2185,             // Japanese Yen
  chf_try: 37.15,              // Swiss Franc
  rub_try: 0.354,              // Russian Ruble
  sar_try: 8.67,               // Saudi Riyal
  ons_usd: 2350.00,
  silver_ons_usd: 28.50,
  platinum_ons_usd: 1015.00,   // Platinum spot — Feb 2026 realistic
  palladium_ons_usd: 970.00,   // Palladium spot — Feb 2026 realistic
  // Crypto (USD) — Feb 2026 realistic
  btc_usd: 96500.00,           // Bitcoin
  eth_usd: 3420.00,            // Ethereum
  sol_usd: 148.50,             // Solana
  ada_usd: 0.72,               // Cardano
  dot_usd: 8.15,               // Polkadot
  avax_usd: 42.80,             // Avalanche
  xrp_usd: 2.42,               // Ripple
  doge_usd: 0.185,             // Dogecoin
  link_usd: 22.60,             // Chainlink
  uni_usd:   9.85,             // Uniswap
  ltc_usd:  84.20,             // Litecoin
  // Stock indices — Feb 2026 realistic reference points
  bist100:  10850.00,          // Borsa Istanbul BIST 100
  nyse:     19420.00,          // NYSE Composite
  nasdaq:   19875.00,          // NASDAQ Composite
  nikkei:   39680.00,          // Nikkei 225 (Tokyo, Japan)
  aex:        925.00,          // AEX 25 — Amsterdam (Euronext)
  bel20:     4300.00,          // BEL 20 — Brussels (Euronext)
  psi20:     6580.00,          // PSI 20 — Lisbon (Euronext)
  sse:       3145.00,          // Shanghai SSE Composite
  ftse:      8420.00,          // FTSE 100 (London)
};

const TROY_OZ_GRAMS = 31.1034768;

// Deterministic tiny jitter for the "live" feel — based on minute of the hour
function jitter(seed, amplitude = 0.004) {
  // returns a multiplier near 1.0 ± amplitude
  const s = Math.sin(seed * 9.301 + 0.713) * 10000;
  return 1 + ((s - Math.floor(s)) - 0.5) * 2 * amplitude;
}

function NeonPriceCard({
  testid, title, subtitle, primaryValue, primaryUnit,
  secondary, change, color, icon: Icon, large, isLight,
  labelColor: customLabelColor, labelShadow, labelWeight = 700,
}) {
  const isUp = change >= 0;
  // Matte palette — no glow, no shimmer.
  const cardBg     = isLight ? "#FFFFFF" : "#0A0A0A";
  const cardBorder = isLight ? "#E2E8F0" : "#1F1F1F";
  // Thematic label color (Gold/Silver/Finance-Blue) overrides default muted slate.
  const headColor  = customLabelColor || (isLight ? "#475569" : "#94A3B8");
  const subColor   = isLight ? "#64748B" : "#94A3B8";
  const valueColor = isLight ? "#1E293B" : "#F1F5F9";  // matte charcoal / matte white
  const unitColor  = isLight ? "#64748B" : "#94A3B8";
  const upColor    = isLight ? "#059669" : "#34D399";  // muted green
  const downColor  = isLight ? "#DC2626" : "#F87171";  // muted red
  return (
    <div
      data-testid={testid}
      className={`border p-4 transition-colors ${large ? "lg:col-span-2" : ""}`}
      style={{
        backgroundColor: cardBg,
        borderColor: cardBorder,
        boxShadow: "none",
      }}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: headColor }}/>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]"
              style={{
                color: headColor,
                fontWeight: labelWeight,
                textShadow: labelShadow || (isLight ? `0 0 0.5px ${headColor}` : "none"),
              }}>
              {title}
            </div>
            {subtitle && (
              <div className="font-mono text-[10px] uppercase tracking-wider mt-0.5"
                style={{ color: headColor, fontWeight: labelWeight, opacity: 1, textShadow: labelShadow || "none" }}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 font-mono text-[11px] font-semibold"
          style={{ color: isUp ? upColor : downColor }}>
          {isUp ? <TrendingUp className="w-3.5 h-3.5"/> : <TrendingDown className="w-3.5 h-3.5"/>}
          {isUp ? "+" : ""}{change.toFixed(2)}%
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div
          className="tracking-tight"
          style={{
            color: valueColor,
            fontSize: large ? "1.4rem" : "1.2rem",
            lineHeight: 1.1,
            fontFamily: "'IBM Plex Mono', monospace",
            fontWeight: 600,
            textShadow: "none",
            letterSpacing: "-0.01em",
          }}>
          {primaryValue}
        </div>
        <div className="font-mono text-xs font-semibold" style={{ color: unitColor }}>
          {primaryUnit}
        </div>
      </div>
      {secondary && (
        <div className="mt-1.5 font-mono text-[11px] font-medium" style={{ color: subColor }}>
          {secondary}
        </div>
      )}
    </div>
  );
}

export default function EkonomiView({ conditions }) {
  const [tick, setTick] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(new Date());
  const { lang } = useI18n();
  const [phoneClock, setPhoneClock] = useState(() => formatPhoneDate(new Date(), lang));
  useEffect(() => {
    setPhoneClock(formatPhoneDate(new Date(), lang));
    const id = setInterval(() => setPhoneClock(formatPhoneDate(new Date(), lang)), 30000);
    return () => clearInterval(id);
  }, [lang]);
  const isLight = conditions?._mode === "day";
  // Diversified premium-dashboard palette — each asset class has its signature.
  // ALL currency pairs unified under PURPLE for forex section consistency.
  // Gold matches the U-VALUE result color (#FFCC00) + glow used in MetricsPanel,
  // creating visual unity between thermal & financial "premium" indicators.
  // Silver keeps vibrant pink for chic identity.
  const GOLD_LABEL      = "#FFCC00";   // same as U-VALUE borderline color
  const GOLD_SHADOW     = "0 0 8px #FFCC0055";  // identical glow as U-VALUE
  const SILVER_LABEL    = "#EC4899";   // vibrant pink — chic, distinct identity
  const PLATINUM_LABEL  = "#00CED1";   // turquoise — Precious Metals Rainbow
  const PALLADIUM_LABEL = "#A855F7";   // elite purple — Precious Metals Rainbow
  const FX_LABEL        = "#1E40AF";   // unified royal blue — ALL currency pairs
  const USD_LABEL       = FX_LABEL;
  const FX_HDR_LABEL    = FX_LABEL;

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
      setUpdatedAt(new Date());
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const rates = useMemo(() => {
    const seed = tick;
    const usd_try = BASE.usd_try * jitter(seed * 1.1);
    const eur_try = BASE.eur_try * jitter(seed * 1.3 + 7);
    const eur_usd = BASE.eur_usd * jitter(seed * 0.9 + 3, 0.002);
    const gbp_try = BASE.gbp_try * jitter(seed * 1.05 + 11);
    // Extended forex vs TRY
    const cny_try = BASE.cny_try * jitter(seed * 1.15 + 59, 0.005);
    const jpy_try = BASE.jpy_try * jitter(seed * 1.25 + 61, 0.006);
    const chf_try = BASE.chf_try * jitter(seed * 1.10 + 67, 0.005);
    const rub_try = BASE.rub_try * jitter(seed * 1.35 + 71, 0.012);
    const sar_try = BASE.sar_try * jitter(seed * 1.08 + 73, 0.004);
    const ons_usd = BASE.ons_usd * jitter(seed * 1.4 + 5, 0.006);
    const silver_ons_usd = BASE.silver_ons_usd * jitter(seed * 1.2 + 9, 0.012);
    const platinum_ons_usd = BASE.platinum_ons_usd * jitter(seed * 1.6 + 13, 0.010);
    const palladium_ons_usd = BASE.palladium_ons_usd * jitter(seed * 1.7 + 17, 0.014);

    // Crypto (higher amplitude — crypto is volatile)
    const btc_usd = BASE.btc_usd * jitter(seed * 2.1 + 19, 0.025);
    const eth_usd = BASE.eth_usd * jitter(seed * 2.3 + 23, 0.028);
    const sol_usd = BASE.sol_usd * jitter(seed * 2.5 + 47, 0.030);
    const ada_usd = BASE.ada_usd * jitter(seed * 2.6 + 79, 0.032);
    const dot_usd = BASE.dot_usd * jitter(seed * 2.4 + 83, 0.028);
    const avax_usd = BASE.avax_usd * jitter(seed * 2.7 + 89, 0.034);
    const xrp_usd = BASE.xrp_usd * jitter(seed * 2.7 + 53, 0.028);
    const doge_usd = BASE.doge_usd * jitter(seed * 2.8 + 97, 0.036);
    const link_usd = BASE.link_usd * jitter(seed * 2.55 + 113, 0.030);
    const uni_usd  = BASE.uni_usd  * jitter(seed * 2.65 + 127, 0.032);
    const ltc_usd  = BASE.ltc_usd  * jitter(seed * 2.35 + 131, 0.024);

    // Stock indices
    const bist100 = BASE.bist100 * jitter(seed * 1.3 + 29, 0.008);
    const nyse    = BASE.nyse    * jitter(seed * 1.1 + 31, 0.005);
    const nasdaq  = BASE.nasdaq  * jitter(seed * 1.4 + 37, 0.007);
    const nikkei  = BASE.nikkei  * jitter(seed * 1.25 + 101, 0.009);
    const aex     = BASE.aex     * jitter(seed * 1.15 + 103, 0.006);
    const bel20   = BASE.bel20   * jitter(seed * 1.18 + 107, 0.006);
    const psi20   = BASE.psi20   * jitter(seed * 1.22 + 109, 0.007);
    const sse     = BASE.sse     * jitter(seed * 1.2 + 41, 0.006);
    const ftse    = BASE.ftse    * jitter(seed * 1.0 + 43, 0.005);

    const gold_gram_try = (ons_usd / TROY_OZ_GRAMS) * usd_try;
    const gold_ceyrek_try = gold_gram_try * 1.75 * 1.04;   // Çeyrek: 1.75g + ~4% premium
    const gold_yarim_try  = gold_gram_try * 3.50 * 1.04;   // Yarım:  3.50g + ~4% premium
    const gold_tam_try    = gold_gram_try * 7.00 * 1.04;   // Tam:    7.00g + ~4% premium
    const gold_ons_try    = ons_usd * usd_try;             // Ons: full troy ounce in TRY
    const silver_gram_try = (silver_ons_usd / TROY_OZ_GRAMS) * usd_try;
    const platinum_gram_try = (platinum_ons_usd / TROY_OZ_GRAMS) * usd_try;
    const palladium_gram_try = (palladium_ons_usd / TROY_OZ_GRAMS) * usd_try;

    return {
      usd_try, eur_try, eur_usd, gbp_try, cny_try, jpy_try, chf_try, rub_try, sar_try,
      ons_usd, silver_ons_usd, platinum_ons_usd, palladium_ons_usd,
      gold_gram_try, gold_ceyrek_try, gold_yarim_try, gold_tam_try, gold_ons_try, silver_gram_try, platinum_gram_try, palladium_gram_try,
      btc_usd, eth_usd, sol_usd, ada_usd, dot_usd, avax_usd, xrp_usd, doge_usd, link_usd, uni_usd, ltc_usd,
      bist100, nyse, nasdaq, nikkei, aex, bel20, psi20, sse, ftse,
      // Synthetic 24h change %
      ch_usd_try: ((usd_try / BASE.usd_try) - 1) * 100 + 0.32,
      ch_eur_try: ((eur_try / BASE.eur_try) - 1) * 100 - 0.18,
      ch_eur_usd: ((eur_usd / BASE.eur_usd) - 1) * 100 - 0.04,
      ch_gbp_try: ((gbp_try / BASE.gbp_try) - 1) * 100 + 0.45,
      ch_cny_try: ((cny_try / BASE.cny_try) - 1) * 100 + 0.14,
      ch_jpy_try: ((jpy_try / BASE.jpy_try) - 1) * 100 - 0.27,
      ch_chf_try: ((chf_try / BASE.chf_try) - 1) * 100 + 0.36,
      ch_rub_try: ((rub_try / BASE.rub_try) - 1) * 100 - 0.51,
      ch_sar_try: ((sar_try / BASE.sar_try) - 1) * 100 + 0.28,
      ch_gold:    ((ons_usd / BASE.ons_usd) - 1) * 100 + 0.78,
      ch_silver:  ((silver_ons_usd / BASE.silver_ons_usd) - 1) * 100 - 0.21,
      ch_platinum:  ((platinum_ons_usd / BASE.platinum_ons_usd) - 1) * 100 + 0.34,
      ch_palladium: ((palladium_ons_usd / BASE.palladium_ons_usd) - 1) * 100 - 0.62,
      ch_btc:     ((btc_usd / BASE.btc_usd) - 1) * 100 + 1.85,
      ch_eth:     ((eth_usd / BASE.eth_usd) - 1) * 100 + 1.22,
      ch_sol:     ((sol_usd / BASE.sol_usd) - 1) * 100 + 2.15,
      ch_ada:     ((ada_usd / BASE.ada_usd) - 1) * 100 + 0.94,
      ch_dot:     ((dot_usd / BASE.dot_usd) - 1) * 100 - 0.42,
      ch_avax:    ((avax_usd / BASE.avax_usd) - 1) * 100 + 1.68,
      ch_xrp:     ((xrp_usd / BASE.xrp_usd) - 1) * 100 - 0.82,
      ch_doge:    ((doge_usd / BASE.doge_usd) - 1) * 100 + 3.24,
      ch_link:    ((link_usd / BASE.link_usd) - 1) * 100 + 1.42,
      ch_uni:     ((uni_usd  / BASE.uni_usd ) - 1) * 100 - 0.68,
      ch_ltc:     ((ltc_usd  / BASE.ltc_usd ) - 1) * 100 + 0.94,
      ch_bist100: ((bist100 / BASE.bist100) - 1) * 100 + 0.68,
      ch_nyse:    ((nyse    / BASE.nyse   ) - 1) * 100 + 0.42,
      ch_nasdaq:  ((nasdaq  / BASE.nasdaq ) - 1) * 100 + 0.91,
      ch_nikkei:  ((nikkei  / BASE.nikkei ) - 1) * 100 + 0.57,
      ch_aex:     ((aex     / BASE.aex    ) - 1) * 100 + 0.28,
      ch_bel20:   ((bel20   / BASE.bel20  ) - 1) * 100 + 0.19,
      ch_psi20:   ((psi20   / BASE.psi20  ) - 1) * 100 - 0.14,
      ch_sse:     ((sse     / BASE.sse    ) - 1) * 100 - 0.24,
      ch_ftse:    ((ftse    / BASE.ftse   ) - 1) * 100 + 0.19,
    };
  }, [tick]);

  const fmt = (v, d=2) => v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
  const fmtTRY = (v) => v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <section className="border-2"
      style={{
        borderColor: isLight ? "#CBD5E1" : "#39FF14",
        backgroundColor: isLight ? "#FFFFFF" : "#000000",
        position: "relative",
      }}
      data-testid="ekonomi-view">

      {/* Always-white floating clock — positioned to overlap the dark FİNANS
          header strip so the white text is guaranteed visible in BOTH modes.
          No theme-conditional color: the strip behind it is always dark. */}
      <div
        key={`clock-${isLight ? "light" : "dark"}`}
        className="keep-shadow-light"
        style={{
          position: "absolute",
          top: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "4px 10px",
          color: "#FFFFFF",
          opacity: 1,
          visibility: "visible",
          pointerEvents: "auto",
          whiteSpace: "nowrap",
          background: "transparent",
          border: "none",
        }}
        data-testid="finance-floating-clock"
        data-mode={isLight ? "light" : "dark"}>
        <CalendarDays
          className="w-4 h-4 shrink-0"
          style={{
            color: "#FFFFFF",
            stroke: "#FFFFFF",
            opacity: 1,
          }}/>
        <span style={{
          fontFamily: "-apple-system, 'SF Pro Display', system-ui, 'Segoe UI', sans-serif",
          fontWeight: 400,
          fontSize: "0.9rem",
          letterSpacing: "0.5px",
          color: "#FFFFFF",
          opacity: 1,
          textShadow: "0 0 4px rgba(0,0,0,0.5)",
        }}>
          {phoneClock}
        </span>
      </div>

      <header className="px-4 py-3 flex items-center justify-between"
        style={{
          backgroundColor: isLight ? "#1E293B" : "#39FF14",
          color: isLight ? "#F1F5F9" : "#000000",
        }}>
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5"/>
          <span className="font-mono text-sm uppercase tracking-[0.22em] font-extrabold">
            FİNANS · Live Markets &amp; Precious Metals
          </span>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider font-bold">
          <span className="w-2 h-2 pulse-live" style={{ backgroundColor: isLight ? "#F1F5F9" : "#000000" }}/>
          LIVE · {updatedAt.toLocaleTimeString("en-GB")}
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* ═══ GLOBAL FINANCE TERMINAL — Multi-Column Precious Metals & Global Markets ═══   */}
        <div
          data-testid="ekonomi-terminal-3col"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
          style={{
            border: `1px solid ${isLight ? "#CBD5E1" : "#00FFFF33"}`,
            borderRadius: 10,
            padding: 14,
            background: isLight ? "#F8FAFC" : "#050505",
            boxShadow: isLight ? "none" : "inset 0 0 24px rgba(0,255,255,0.04)",
          }}>
          <TerminalColumn isLight={isLight} title="ALTIN · GOLD" accent="#FFCC00" testid="terminal-col-gold"
            rows={[
              { k: "Ons Altın",            v: fmtTRY(rates.gold_ons_try),           u: "₺", ch: rates.ch_gold },
              { k: "24 Ayar Gram Altın",   v: fmtTRY(rates.gold_gram_try),          u: "₺", ch: rates.ch_gold },
              { k: "22 Ayar Gram Altın",   v: fmtTRY(rates.gold_gram_try * 0.916),  u: "₺", ch: rates.ch_gold },
              { k: "18 Ayar Gram Altın",   v: fmtTRY(rates.gold_gram_try * 0.750),  u: "₺", ch: rates.ch_gold },
              { k: "14 Ayar Gram Altın",   v: fmtTRY(rates.gold_gram_try * 0.585),  u: "₺", ch: rates.ch_gold },
              { k: "Çeyrek Altın",         v: fmtTRY(rates.gold_ceyrek_try),        u: "₺", ch: rates.ch_gold },
              { k: "Yarım Altın",          v: fmtTRY(rates.gold_yarim_try),         u: "₺", ch: rates.ch_gold },
              { k: "Tam Altın",            v: fmtTRY(rates.gold_tam_try),           u: "₺", ch: rates.ch_gold },
              { k: "Külçe Altın (Bullion)",v: fmtTRY(rates.gold_ons_try * 32.15),   u: "₺", ch: rates.ch_gold },
            ]}/>
          {/* ═══ DİĞER DEĞERLİ METALLER — Silver, Platinum, Palladium grouped vertically ═══ */}
          <TerminalColumn isLight={isLight} title="DEĞERLİ METALLER · PRECIOUS METALS" accent="#EC4899" testid="terminal-col-precious"
            rows={[
              { section: "GÜMÜŞ · Silver", accent: "#EC4899" },
              { k: "Ons Gümüş",     v: fmt(rates.silver_ons_usd, 2),     u: "$", ch: rates.ch_silver },
              { k: "Gram Gümüş",    v: fmtTRY(rates.silver_gram_try),    u: "₺", ch: rates.ch_silver },
              { section: "PLATİN · Platinum", accent: "#A855F7" },
              { k: "Ons Platin",    v: fmt(rates.platinum_ons_usd, 2),   u: "$", ch: rates.ch_platinum },
              { k: "Gram Platin",   v: fmtTRY(rates.platinum_gram_try),  u: "₺", ch: rates.ch_platinum },
              { section: "PALADYUM · Palladium", accent: "#CED4DA" },
              { k: "Ons Paladyum",  v: fmt(rates.palladium_ons_usd, 2),  u: "$", ch: rates.ch_palladium },
              { k: "Gram Paladyum", v: fmtTRY(rates.palladium_gram_try), u: "₺", ch: rates.ch_palladium },
            ]}/>
          <TerminalColumn isLight={isLight} title="DÖVİZ KURLARI · FOREX" accent="#1E40AF" testid="terminal-col-forex"
            rows={[
              { k: "USD / TRY", v: fmt(rates.usd_try, 4), u: "₺", ch: rates.ch_usd_try },
              { k: "EUR / TRY", v: fmt(rates.eur_try, 4), u: "₺", ch: rates.ch_eur_try },
              { k: "GBP / TRY", v: fmt(rates.gbp_try, 4), u: "₺", ch: rates.ch_gbp_try },
              { k: "CHF / TRY", v: fmt(rates.chf_try, 4), u: "₺", ch: rates.ch_chf_try },
              { k: "CNY / TRY", v: fmt(rates.cny_try, 4), u: "₺", ch: rates.ch_cny_try },
              { k: "JPY / TRY", v: fmt(rates.jpy_try, 4), u: "₺", ch: rates.ch_jpy_try },
              { k: "SAR / TRY", v: fmt(rates.sar_try, 4), u: "₺", ch: rates.ch_sar_try },
              { k: "RUB / TRY", v: fmt(rates.rub_try, 4), u: "₺", ch: rates.ch_rub_try },
              { k: "EUR / USD", v: fmt(rates.eur_usd, 4), u: "$", ch: rates.ch_eur_usd },
            ]}/>
          <TerminalColumn isLight={isLight} title="KRİPTO · CRYPTO" accent="#FB923C" testid="terminal-col-crypto"
            rows={[
              { k: "BTC · Bitcoin",   v: fmt(rates.btc_usd, 0),  u: "$", ch: rates.ch_btc },
              { k: "ETH · Ethereum",  v: fmt(rates.eth_usd, 2),  u: "$", ch: rates.ch_eth },
              { k: "SOL · Solana",    v: fmt(rates.sol_usd, 2),  u: "$", ch: rates.ch_sol },
              { k: "ADA · Cardano",   v: fmt(rates.ada_usd, 4),  u: "$", ch: rates.ch_ada },
              { k: "DOT · Polkadot",  v: fmt(rates.dot_usd, 3),  u: "$", ch: rates.ch_dot },
              { k: "AVAX · Avalanche",v: fmt(rates.avax_usd, 2), u: "$", ch: rates.ch_avax },
              { k: "XRP · Ripple",    v: fmt(rates.xrp_usd, 3),  u: "$", ch: rates.ch_xrp },
              { k: "DOGE · Dogecoin", v: fmt(rates.doge_usd, 4), u: "$", ch: rates.ch_doge },
              { k: "LINK · Chainlink",v: fmt(rates.link_usd, 2), u: "$", ch: rates.ch_link },
              { k: "UNI · Uniswap",   v: fmt(rates.uni_usd, 2),  u: "$", ch: rates.ch_uni },
              { k: "LTC · Litecoin",  v: fmt(rates.ltc_usd, 2),  u: "$", ch: rates.ch_ltc },
            ]}/>
          <TerminalColumn isLight={isLight} title="KÜRESEL PİYASALAR · GLOBAL MARKETS" accent="#00FFFF" testid="terminal-col-markets"
            rows={[
              { k: "NYSE (New York)",              v: fmt(rates.nyse, 2),    u: "pts", ch: rates.ch_nyse },
              { k: "NASDAQ",                       v: fmt(rates.nasdaq, 2),  u: "pts", ch: rates.ch_nasdaq },
              { k: "Londra (FTSE 100)",            v: fmt(rates.ftse, 2),    u: "pts", ch: rates.ch_ftse },
              { k: "Japonya (Nikkei 225)",         v: fmt(rates.nikkei, 2),  u: "pts", ch: rates.ch_nikkei },
              { k: "Amsterdam (AEX · Euronext)",   v: fmt(rates.aex, 2),     u: "pts", ch: rates.ch_aex },
              { k: "Brüksel (BEL 20 · Euronext)",  v: fmt(rates.bel20, 2),   u: "pts", ch: rates.ch_bel20 },
              { k: "Lizbon (PSI 20 · Euronext)",   v: fmt(rates.psi20, 2),   u: "pts", ch: rates.ch_psi20 },
              { k: "BIST 100 (İstanbul)",          v: fmt(rates.bist100, 2), u: "₺",   ch: rates.ch_bist100 },
              { k: "SSE (Şanghay)",                v: fmt(rates.sse, 2),     u: "pts", ch: rates.ch_sse },
            ]}/>
        </div>

        {/* Legacy sections (Currencies / Gold / Stock Markets) removed —
            all finance data now lives in the 5-column terminal above. */}
        {/* Disclaimer */}
        <div className="border px-4 py-3"
          style={{
            borderColor: isLight ? "#E2E8F0" : "#1F1F1F",
            backgroundColor: isLight ? "#F8FAFC" : "#0A0A0A",
          }}
          data-testid="ekonomi-disclaimer">
          <div className="font-mono text-[10px] uppercase tracking-wider font-medium"
            style={{ color: isLight ? "#64748B" : "#94A3B8" }}>
            ⚠ Simulated reference rates · Feb 2026 baseline · Refreshes every 4 s.
            For live trading data, wire a market-data API key (e.g. exchangerate.host, metals.live, OpenExchangeRates) — currently
            running on deterministic jitter so engineering decisions are reproducible.
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 3-Column Terminal Column — vertical list with header + rows
// ─────────────────────────────────────────────────────────────────────────
function TerminalColumn({ isLight, title, accent, rows, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 12,
        borderRadius: 8,
        background: isLight ? "#FFFFFF" : "#000000",
        border: `1px solid ${isLight ? "#E2E8F0" : `${accent}55`}`,
        boxShadow: isLight ? "none" : `0 0 12px ${accent}22, inset 0 0 8px ${accent}11`,
        minHeight: 220,
      }}>
      {/* Column Header — dual-language ready (letter-spacing tuned for longer titles) */}
      <div
        style={{
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          fontSize: 12,
          fontWeight: 900,
          letterSpacing: "0.14em",
          color: accent,
          textShadow: isLight ? "none" : `0 0 8px ${accent}88`,
          paddingBottom: 8,
          borderBottom: `1px dashed ${accent}44`,
          marginBottom: 4,
          textAlign: "center",
          lineHeight: 1.25,
        }}>
        ◆ {title}
      </div>
      {/* Rows */}
      {rows.map((r, i) => {
        // Sub-section separator (used in DEĞERLİ METALLER combined column)
        if (r.section) {
          const secAccent = r.accent || accent;
          return (
            <div key={`sec-${i}`}
              data-testid={`${testid}-section-${i}`}
              style={{
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: 14,
                fontWeight: 900,
                letterSpacing: "0.18em",
                color: secAccent,
                textShadow: isLight
                  ? `0 1px 0 rgba(0,0,0,0.08)`
                  : `0 0 10px ${secAccent}AA, 0 0 2px ${secAccent}`,
                padding: "10px 8px 8px",
                marginTop: i === 0 ? 2 : 10,
                background: isLight
                  ? `${secAccent}14`
                  : `linear-gradient(90deg, ${secAccent}22 0%, transparent 100%)`,
                borderLeft: `4px solid ${secAccent}`,
                borderTop: i === 0 ? "none" : `1px dashed ${isLight ? "#CBD5E1" : `${secAccent}44`}`,
                borderRadius: 4,
                textAlign: "left",
                textTransform: "uppercase",
              }}>
              ◆ {r.section}
            </div>
          );
        }
        const up = r.ch >= 0;
        const chColor = up ? "#10B981" : "#EF4444";
        return (
          <div key={i}
            data-testid={`${testid}-row-${i}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "8px 10px",
              borderRadius: 6,
              background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.02)",
              border: `1px solid ${isLight ? "#E2E8F0" : "rgba(255,255,255,0.05)"}`,
            }}>
            <div style={{
              fontSize: 11,
              fontWeight: 600,
              color: isLight ? "#0F172A" : "#E2E8F0",
              letterSpacing: "0.02em",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {r.k}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                fontWeight: 800,
                color: isLight ? "#0F172A" : "#FFFFFF",
                lineHeight: 1.1,
              }}>
                {r.v} <span style={{ fontSize: 10, fontWeight: 600, color: isLight ? "#64748B" : "#94A3B8", marginLeft: 2 }}>{r.u}</span>
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                fontWeight: 700,
                color: chColor,
                marginTop: 2,
                textShadow: isLight ? "none" : `0 0 6px ${chColor}66`,
              }}>
                {up ? "▲" : "▼"} {Math.abs(r.ch).toFixed(2)}%
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
